import React, { useState, useEffect } from "react";
import { format, parse, isValid } from "date-fns";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import { apiFetch } from "../utils/api";
import DivisionPositionModal from "./DivisionPositionModal";
import "./ShiftDetailModal.css";
import ManualCheckInModal from "./ManualCheckInModal";
import RichAuditsPanel from "./RichAuditsPanel";
import DuplicateShiftModal from "./DuplicateShiftModal";
import DateTimePickerPanel from "./DateTimePicker.jsx";
import {
  normalizeShiftTravelType,
  travelCheckboxLabel,
  shiftRowHasTravel,
} from "../utils/shiftTravelDisplay";
import { useAppPermissionsOptional } from "../context/PermissionContext";
import { FaFlag, FaCar } from "react-icons/fa";
import { MdDoNotDisturbOff } from "react-icons/md";
import CarpoolEditModal from "./CarpoolEditModal";
import StaffAvatarCircle from "./StaffAvatarCircle";
import { shiftStaffEmployeeForAvatar } from "../utils/accountAvatarUrl";
import { isSubmittedActualState } from "../utils/shiftActualState";
import {
  formatAppDateTime,
  fromDatetimeLocalValue,
  hasAppDateTime,
  instantFromAppWallParts,
  parseDbDateTime,
  toAppDateKey,
  toDatetimeLocalValue,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import {
  formatAccountTypeLabel,
  formatDurationAfterBreak,
  formatShiftDateTime,
  formatCheckInTimeLine,
} from "../utils/shiftListStatusDisplay";
import ShiftActualHistoryCell from "./shifts/ShiftActualHistoryCell";

const SHIFT_DATETIME_DISPLAY_FMT = "MMM d, yyyy h:mm a";

const DISPLAY_MONTH_INDEX = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

function formatShiftInstantDisplay(value, { compact = false } = {}) {
  if (!value) return "";
  const formatted = formatAppDateTime(value, { emptyValue: "" });
  if (!compact) return formatted;
  return formatted.replace(/, \d{4}/, "").replace(", ", " · ");
}

function shiftDisplayToDate(str) {
  if (!str || !String(str).trim()) return new Date();
  const s = String(str).trim();
  const fromDb = parseDbDateTime(s);
  if (fromDb) return fromDb;

  const displayMatch = s.match(
    /^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(am|pm)$/i,
  );
  if (displayMatch) {
    let hour = Number(displayMatch[4]);
    const minute = Number(displayMatch[5]);
    const ampm = displayMatch[6].toLowerCase();
    if (ampm === "pm" && hour !== 12) hour += 12;
    if (ampm === "am" && hour === 12) hour = 0;
    const month = DISPLAY_MONTH_INDEX[displayMatch[1].slice(0, 3).toLowerCase()];
    if (month) {
      const instant = instantFromAppWallParts({
        year: Number(displayMatch[3]),
        month,
        day: Number(displayMatch[2]),
        hour,
        minute,
      });
      if (instant) return instant;
    }
  }

  const d = parse(s, SHIFT_DATETIME_DISPLAY_FMT, new Date());
  return isValid(d) ? d : new Date();
}

/** MySQL DATETIME UTC wall string from display-formatted shift time. */
function shiftDisplayToMysqlDateTime(str) {
  if (!str || !String(str).trim()) return null;
  const instant = shiftDisplayToDate(str);
  return instant ? toMysqlUtcWallString(instant) : null;
}

function normalizeRoundingMode(value) {
  const mode = String(value || "nearest").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["up", "increase", "ceil", "round_up", "roundup"].includes(mode)) return "up";
  if (["down", "decrease", "floor", "round_down", "rounddown"].includes(mode)) return "down";
  return "nearest";
}

function isRoundingEnabledValue(value) {
  if (value === true) return true;
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "true") return true;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
}

function normalizeShiftRoundingSettings(data) {
  const graceTime = Number(data?.grace_time ?? data?.graceTime);
  const roundingRaw = data?.grace_time_rounding ?? data?.graceTimeRounding;
  const intervalMinutes = Number(roundingRaw);
  const enabled =
    isRoundingEnabledValue(roundingRaw) &&
    Number.isFinite(intervalMinutes) &&
    intervalMinutes > 0;
  return {
    enabled,
    intervalMinutes: enabled ? intervalMinutes : 0,
    graceWindowMinutes: Number.isFinite(graceTime) && graceTime > 0 ? graceTime : 0,
    checkInMode: normalizeRoundingMode(data?.check_in_rounding ?? data?.checkInRounding),
    checkOutMode: normalizeRoundingMode(data?.check_out_rounding ?? data?.checkOutRounding),
  };
}

function applyScheduledGraceSnap(date, settings, options = {}) {
  if (!date || !settings?.enabled) return { date, snapped: false };
  const graceWindowMinutes = Number(settings.graceWindowMinutes);
  if (!Number.isFinite(graceWindowMinutes) || graceWindowMinutes <= 0) {
    return { date, snapped: false };
  }

  const scheduledValue =
    options.field === "checkin" ? options.scheduledStart : options.scheduledEnd;
  const scheduledDate = parseDbDateTime(scheduledValue) || new Date(scheduledValue);
  if (!scheduledDate || Number.isNaN(scheduledDate.getTime())) {
    return { date, snapped: false };
  }

  const diffMinutes = (date.getTime() - scheduledDate.getTime()) / 60000;
  if (options.field === "checkin" && diffMinutes >= 0 && diffMinutes <= graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  if (options.field === "checkout" && diffMinutes <= 0 && diffMinutes >= -graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  return { date, snapped: false };
}

function roundDateByShiftSettings(date, mode, settings, options = {}) {
  if (!date || !settings?.enabled) return date;
  const intervalMinutes = Number(settings.intervalMinutes);
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) return date;

  const snapResult = applyScheduledGraceSnap(date, settings, options);
  if (snapResult.snapped) return snapResult.date;
  if (intervalMinutes === 1) {
    const exactMinute = new Date(snapResult.date);
    exactMinute.setSeconds(0, 0);
    return exactMinute;
  }

  const stepMs = intervalMinutes * 60000;
  const currentMs = snapResult.date.getTime();
  const normalizedMode = normalizeRoundingMode(mode);
  let roundedMs = currentMs;

  if (normalizedMode === "up") {
    roundedMs = Math.ceil(currentMs / stepMs) * stepMs;
  } else if (normalizedMode === "down") {
    roundedMs = Math.floor(currentMs / stepMs) * stepMs;
  } else {
    roundedMs = Math.round(currentMs / stepMs) * stepMs;
  }

  return new Date(roundedMs);
}

function getShiftClientId(shiftRow) {
  return (
    shiftRow?.client_id ||
    shiftRow?.clientId ||
    shiftRow?.clientID ||
    shiftRow?.client?.clientId ||
    shiftRow?.client?.id ||
    shiftRow?.client?.client_id ||
    shiftRow?.clientDetails?.clientId ||
    shiftRow?.clientDetails?.client_id ||
    shiftRow?.job?.client_id ||
    shiftRow?.job?.clientId ||
    shiftRow?.cl_id ||
    null
  );
}

async function loadShiftRoundingSettingsForClient(clientId) {
  if (!clientId) {
    console.warn("[manualAttendance] shift settings skipped: missing client id");
    return null;
  }
  try {
    console.log("[manualAttendance] loading shift settings", { clientId });
    const res = await apiFetch(`/api/clients/${encodeURIComponent(clientId)}/shift-settings`);
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn("[manualAttendance] shift settings fetch failed", {
        clientId,
        status: res.status,
        payload,
      });
      return null;
    }
    console.log("[manualAttendance] shift settings loaded", {
      clientId,
      settings: payload?.data,
    });
    return normalizeShiftRoundingSettings(payload?.data);
  } catch (err) {
    console.warn("Failed to load shift rounding settings for manual attendance", err);
    return null;
  }
}

async function roundManualAttendanceDate(date, attendanceMode, shiftRow) {
  const parsed = parseDbDateTime(date) || new Date(date);
  if (!parsed || Number.isNaN(parsed.getTime())) return null;

  const clientId = getShiftClientId(shiftRow);
  console.log("[manualAttendance] rounding selected time", {
    shiftId: shiftRow?.shift_id || shiftRow?.id,
    clientId,
    attendanceMode,
    selected: date,
  });
  const settings = await loadShiftRoundingSettingsForClient(clientId);
  const mode =
    attendanceMode === "checkout"
      ? settings?.checkOutMode
      : settings?.checkInMode;
  return roundDateByShiftSettings(parsed, mode, settings, {
    field: attendanceMode === "checkout" ? "checkout" : "checkin",
    scheduledStart:
      shiftRow?.start_date ||
      shiftRow?.startDateIso ||
      shiftRow?.startDate ||
      null,
    scheduledEnd:
      shiftRow?.end_date ||
      shiftRow?.endDateIso ||
      shiftRow?.endDate ||
      null,
  });
}

async function roundManualAttendanceTimestamp(date, attendanceMode, shiftRow) {
  const rounded = await roundManualAttendanceDate(date, attendanceMode, shiftRow);
  if (!rounded) return date;
  return toMysqlUtcWallString(rounded);
}

function getShiftAssignedAccountId(shiftRow) {
  if (!shiftRow) return null;
  const raw =
    shiftRow.assigned_staff_id ??
    shiftRow.assigned_account_id ??
    shiftRow.staff_account_id ??
    shiftRow.account_id ??
    shiftRow.accountId ??
    shiftRow.shiftAccountId ??
    shiftRow.shift_account_id ??
    shiftRow.staff_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function shiftHasAssignedStaffAccount(shiftRow) {
  return getShiftAssignedAccountId(shiftRow) != null;
}

/** Status dropdown: merge shifts.state with assignment_ui_status (Schedule can lag or vice versa). */
export function deriveModalStatusForForm(shiftRow) {
  if (!shiftRow) return "unassigned";
  const term = String(shiftRow.state ?? "")
    .trim()
    .toLowerCase();
  if (["cancelled", "declined", "deleted"].includes(term)) return term;

  const ui = String(
    shiftRow.assignment_ui_status ?? shiftRow.assignmentUiStatus ?? "",
  )
    .trim()
    .toLowerCase();

  const rank = (s) =>
    ({ confirmed: 3, tentative: 2, unassigned: 1 }[s] || 0);

  if (
    ["tentative", "confirmed", "unassigned"].includes(term) &&
    ["tentative", "confirmed", "unassigned"].includes(ui)
  ) {
    return rank(ui) >= rank(term) ? ui : term;
  }

  if (["unassigned", "tentative", "confirmed"].includes(term)) return term;

  if (term === "assigned") return "tentative";

  if (term === "completed") {
    return shiftHasAssignedStaffAccount(shiftRow) ? "confirmed" : "unassigned";
  }

  if (["unassigned", "tentative", "confirmed"].includes(ui)) return ui;

  if (["unfilled", "none", "active"].includes(term)) {
    return shiftHasAssignedStaffAccount(shiftRow) ? "tentative" : "unassigned";
  }

  return "unassigned";
}

function isFormStatusRequiringUnassignFlow(status) {
  const s = String(status ?? "").trim().toLowerCase();
  return s === "declined" || s === "unassigned";
}

function normalizeActualState(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "_");
}

function actualStateLabel(raw) {
  const state = normalizeActualState(raw);
  if (!isSubmittedActualState(state)) {
    return "Not Submitted";
  }
  if (state === "as_scheduled" || state === "asscheduled") {
    return "As Scheduled";
  }
  if (state === "different") return "Different";
  if (state === "no_show" || state === "noshow") return "No Show";
  return "Not Submitted";
}

function toDateTimeLocalValue(input) {
  if (!input) return "";
  return toDatetimeLocalValue(input);
}

function mergeSkillCertOptions(primary = [], secondary = [], kind = "skill") {
  const seen = new Set();
  const out = [];
  const getId = (item) =>
    kind === "skill"
      ? String(item?.skill_id ?? item?.id ?? "")
      : String(item?.certification_id ?? item?.id ?? "");
  for (const item of [...primary, ...secondary]) {
    const id = getId(item);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

function isIdLikeSkillCertLabel(name, id) {
  const label = String(name ?? "").trim();
  if (!label) return true;
  if (id != null && label === String(id)) return true;
  return /^\d+$/.test(label);
}

function resolveSkillChipLabel(skill, options) {
  if (skill == null) return "";
  if (typeof skill === "string" || typeof skill === "number") {
    const id = String(skill);
    const matched = (options || []).find(
      (o) => String(o.skill_id ?? o.id) === id,
    );
    return matched?.name || id;
  }
  const id = skill.id ?? skill.skill_id ?? skill.skillId;
  const name = skill.name ?? skill.skill_name ?? "";
  if (!isIdLikeSkillCertLabel(name, id)) return String(name);
  const matched = (options || []).find(
    (o) => String(o.skill_id ?? o.id) === String(id),
  );
  return matched?.name || name || (id != null ? String(id) : "");
}

function resolveCertChipLabel(cert, options) {
  if (cert == null) return "";
  if (typeof cert === "string" || typeof cert === "number") {
    const id = String(cert);
    const matched = (options || []).find(
      (o) => String(o.certification_id ?? o.id) === id,
    );
    return matched?.name || id;
  }
  const id = cert.id ?? cert.certification_id ?? cert.certificationId;
  const name = cert.name ?? cert.certification_name ?? "";
  if (!isIdLikeSkillCertLabel(name, id)) return String(name);
  const matched = (options || []).find(
    (o) => String(o.certification_id ?? o.id) === String(id),
  );
  return matched?.name || name || (id != null ? String(id) : "");
}

const ShiftDetailModal = ({
  show,
  onHide,
  onRefresh,
  shiftId,
  initialIsClosedWeek,
  requestDefaults,
  onOpenPersonnelModal,
  overlayZClass = "z-[9999]",
  nestedOverlayZClass = "z-[10000]",
  unassignOverlayZClass = "z-[50300]",
  actualsOverlayZClass = "z-[10020]",
  duplicateZIndexClass = "z-[10001]",
  divisionOverlayZClass = "z-[10001]",
  carpoolOverlayZClass = "z-[10050]",
}) => {
  const appPerms = useAppPermissionsOptional();
  const canWriteShiftActual =
    appPerms == null ? true : appPerms.canWriteShiftActual;

  const [activeTab, setActiveTab] = useState("overview");
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [unassignSaveModal, setUnassignSaveModal] = useState({
    open: false,
    submitting: false,
    assignedAccountId: null,
    staffDisplayName: "",
    countTowardsReliability: true,
    reason: "Other",
    notice24h: "yes",
    availability: "available",
    note: "",
  });
  const [carpoolViewer, setCarpoolViewer] = useState({
    open: false,
    name: "",
    note: "",
  });
  const [carpoolSaving, setCarpoolSaving] = useState(false);
  const [notifyFlagSaving, setNotifyFlagSaving] = useState(false);
  const unassignPendingSaveRef = React.useRef(null);
  const unassignModalSnapshotRef = React.useRef(unassignSaveModal);
  unassignModalSnapshotRef.current = unassignSaveModal;

  const [datetimePickerTarget, setDatetimePickerTarget] = useState(null);

  const openShiftDatePicker = (target) => {
    setDatetimePickerTarget(target);
  };

  const [tempStaffSearch, setTempStaffSearch] = useState("");
  const [tempStaffList] = useState([
    {
      name: "Jessica Kate Lock",
      type: "Field Staff",
      proximity: "8,884.5",
      reliability: "100",
      utilization: "0",
      hours: "0/0",
    },
    {
      name: "Ifechukwu Ojiekwe-Agodi",
      type: "Field Staff",
      proximity: "9,157.7",
      reliability: "100",
      utilization: "0",
      hours: "7/0",
    },
    {
      name: "Neelesh Dharia",
      type: "Field Staff",
      proximity: "0",
      reliability: "100",
      utilization: "0",
      hours: "1/0",
    },
  ]);

  const [formData, setFormData] = useState({
    // default to unassigned so new shifts render clearly; will be reset when shift loads
    status: "unassigned",
    shiftSupervisor: "",
    gender: "any",
    skills: [],
    certifications: "",
    uniform: "",
    noteToTempStaff: "",
    adminNote: "",
    unpaidBreak: false,
    staffNotification: false,
    travelTime: false,
    payRate: "",
    billRate: "",
    startDate: "",
    endDate: "",
    jobPostingId: "",
    travelTimeAmount: "",
    travelTimeType: "flat",
  });

  const unassignAvailabilityOptions = React.useMemo(() => {
    try {
      const start = parse(
        String(formData.startDate || "").trim(),
        SHIFT_DATETIME_DISPLAY_FMT,
        new Date(),
      );
      const end = parse(
        String(formData.endDate || "").trim(),
        SHIFT_DATETIME_DISPLAY_FMT,
        new Date(),
      );
      if (!isValid(start)) {
        return [
          { key: "available", label: "Available during shift" },
          { key: "unavailable_shift", label: "Unavailable during shift" },
          { key: "unavailable_day", label: "Unavailable all day" },
          { key: "unavailable_range", label: "Unavailable date range" },
        ];
      }
      const dayShort = format(start, "MMM d");
      const dayLong = format(start, "MMM d, yyyy");
      const t1 = format(start, "h:mm a").toLowerCase();
      const t2 = isValid(end)
        ? format(end, "h:mm a").toLowerCase()
        : t1;
      return [
        {
          key: "available",
          label: `Available ${dayShort} · ${t1} – ${t2}`,
        },
        {
          key: "unavailable_shift",
          label: `Unavailable ${dayShort} · ${t1} – ${t2}`,
        },
        {
          key: "unavailable_day",
          label: `Unavailable all day ${dayLong}`,
        },
        { key: "unavailable_range", label: "Unavailable date range" },
      ];
    } catch {
      return [
        { key: "available", label: "Available during shift" },
        { key: "unavailable_shift", label: "Unavailable during shift" },
        { key: "unavailable_day", label: "Unavailable all day" },
        { key: "unavailable_range", label: "Unavailable date range" },
      ];
    }
  }, [formData.startDate, formData.endDate]);

  const [supervisorId, setSupervisorId] = useState(null);
  const [supervisorQuery, setSupervisorQuery] = useState("");
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);
  const [skillSearch, setSkillSearch] = useState("");
  const [certSearch, setCertSearch] = useState("");
  const supervisorTimer = React.useRef(null);
  const skillTimer = React.useRef(null);
  const certTimer = React.useRef(null);

  // Dropdown open/close state
  const [supervisorDropdownOpen, setSupervisorDropdownOpen] = useState(false);
  const [skillsDropdownOpen, setSkillsDropdownOpen] = useState(false);
  const [certsDropdownOpen, setCertsDropdownOpen] = useState(false);

  //manual check in
  const [showManualCheckIn, setShowManualCheckIn] = useState(false);
  const [manualAttendanceMode, setManualAttendanceMode] = useState("checkin");
  const [manualAttendanceSaving, setManualAttendanceSaving] = useState(false);
  const [openAttendanceMenu, setOpenAttendanceMenu] = useState(null);
  const [showActualsModal, setShowActualsModal] = useState(false);
  const [showActualsHistoryModal, setShowActualsHistoryModal] = useState(false);
  const [actualsHistory, setActualsHistory] = useState([]);
  const [actualsHistoryLoading, setActualsHistoryLoading] = useState(false);
  const [settingActiveActualId, setSettingActiveActualId] = useState(null);
  const [historyRowMenuId, setHistoryRowMenuId] = useState(null);
  const [actualsSubmitting, setActualsSubmitting] = useState(false);
  const [actualsForm, setActualsForm] = useState({
    actualState: "as_scheduled",
    actualStartDate: "",
    actualEndDate: "",
    actualUnpaidBreakMinutes: "",
    note: "",
  });

  useEffect(() => {
    if (!canWriteShiftActual && showActualsModal) {
      setShowActualsModal(false);
    }
  }, [canWriteShiftActual, showActualsModal]);

  // Refs for click-outside detection
  const supervisorRef = React.useRef(null);
  const skillsRef = React.useRef(null);
  const certsRef = React.useRef(null);
  const attendanceMenuRef = React.useRef(null);
  const weekState = String(shift?.week_state || "").toLowerCase();
  const closedWeekFlag =
    shift?.isClosedWeek === true ||
    shift?.isClosedWeek === 1 ||
    shift?.isClosedWeek === "1";
  const hasInitialClosedWeek =
    initialIsClosedWeek === true ||
    initialIsClosedWeek === false ||
    initialIsClosedWeek === 1 ||
    initialIsClosedWeek === 0 ||
    initialIsClosedWeek === "1" ||
    initialIsClosedWeek === "0";
  const normalizedInitialClosedWeek =
    initialIsClosedWeek === true ||
    initialIsClosedWeek === 1 ||
    initialIsClosedWeek === "1";
  const resolvedClosedWeek = closedWeekFlag || weekState === "closed";
  const detailReady =
    !loading &&
    shift != null &&
    String(shift.shift_id ?? shift.id ?? "") === String(shiftId ?? "");
  // After GET /jobs/shifts/:id, trust accounting week_state / isClosedWeek from the server.
  // Do not keep list-derived initialIsClosedWeek (e.g. Jobs grid YEARWEEK) pinned over the detail payload.
  const isClosedWeek = detailReady
    ? resolvedClosedWeek
    : hasInitialClosedWeek
      ? normalizedInitialClosedWeek
      : resolvedClosedWeek;

  const handleCarpoolSave = async ({ carpool_name, carpool_note }) => {
    if (isClosedWeek || !shiftId) return;
    setCarpoolSaving(true);
    try {
      const res = await apiFetch(`/api/shifts/${shiftId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carpool_name, carpool_note }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Update failed");
      }
      showSuccess("Carpool details saved");
      setCarpoolViewer((s) => ({ ...s, open: false }));
      setShift((prev) =>
        prev
          ? {
              ...prev,
              carpool_name: carpool_name ?? prev.carpool_name,
              carpool_note: carpool_note ?? prev.carpool_note,
            }
          : prev,
      );
      if (typeof onRefresh === "function") onRefresh();
    } catch (e) {
      console.error(e);
      showError(e.message || "Failed to save carpool");
    } finally {
      setCarpoolSaving(false);
    }
  };

  const handleAssignedRowNotifyToggle = async () => {
    if (isClosedWeek || !shiftId || notifyFlagSaving) return;
    const next = !formData.staffNotification;
    setNotifyFlagSaving(true);
    try {
      const res = await apiFetch(`/api/shifts/${shiftId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notify_staff_change: next ? 1 : 0 }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Update failed");
      }
      setFormData((prev) => ({ ...prev, staffNotification: next }));
      setShift((prev) =>
        prev ? { ...prev, notify_staff_change: next ? 1 : 0 } : prev,
      );
      if (typeof onRefresh === "function") onRefresh();
    } catch (e) {
      console.error(e);
      showError(e.message || "Failed to update notification flag");
    } finally {
      setNotifyFlagSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (isClosedWeek) return;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (
        (field === "startDate" || field === "endDate") &&
        prev[field] !== value
      ) {
        next.staffNotification = true;
      }
      return next;
    });
  };

  const clientTravelModeForCheckbox = shift
    ? normalizeShiftTravelType(shift.client_travel_time_type)
    : "none";
  const showTravelTimeCheckbox =
    !!shift &&
    (clientTravelModeForCheckbox === "hourly" ||
      clientTravelModeForCheckbox === "flat");
  const travelTimeCheckboxLabel = travelCheckboxLabel(
    shift?.client_travel_time_type,
    shift?.client_travel_time_amount,
  );

  const applyTravelCheckbox = (checked) => {
    if (isClosedWeek) return;
    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        travelTime: false,
        travelTimeAmount: "",
        travelTimeType: "",
      }));
      return;
    }
    const mode = normalizeShiftTravelType(shift?.client_travel_time_type);
    const amt =
      shift?.client_travel_time_amount != null &&
      shift?.client_travel_time_amount !== ""
        ? String(shift.client_travel_time_amount)
        : "";
    const typeOut = mode === "flat" ? "flat_rate" : "hourly";
    setFormData((prev) => ({
      ...prev,
      travelTime: true,
      travelTimeAmount: amt,
      travelTimeType: typeOut,
    }));
  };

  const removeSkill = (skillToRemove) => {
    if (isClosedWeek) return;
    setFormData((prev) => {
      const updatedSkills = (prev.skills || []).filter((skill) =>
        skill.name ? skill.name !== skillToRemove : skill !== skillToRemove,
      );
      return { ...prev, skills: updatedSkills };
    });
    setShift((prev) => ({
      ...prev,
      skills: (prev.skills || []).filter((skill) =>
        skill.name ? skill.name !== skillToRemove : skill !== skillToRemove,
      ),
    }));
  };

  const removeCert = (certToRemove) => {
    if (isClosedWeek) return;
    setFormData((prev) => {
      const list = Array.isArray(prev.certifications)
        ? prev.certifications
        : [];
      const updatedCerts = list.filter(
        (cert) =>
          (cert.id || cert.certification_id) !==
          (certToRemove.id || certToRemove.certification_id),
      );
      return { ...prev, certifications: updatedCerts };
    });
    setShift((prev) => {
      const list = Array.isArray(prev.certifications)
        ? prev.certifications
        : [];
      return {
        ...prev,
        certifications: list.filter(
          (cert) =>
            (cert.id || cert.certification_id) !==
            (certToRemove.id || certToRemove.certification_id),
        ),
      };
    });
  };

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        supervisorRef.current &&
        !supervisorRef.current.contains(event.target)
      ) {
        setSupervisorDropdownOpen(false);
      }
      if (skillsRef.current && !skillsRef.current.contains(event.target)) {
        setSkillsDropdownOpen(false);
      }
      if (certsRef.current && !certsRef.current.contains(event.target)) {
        setCertsDropdownOpen(false);
      }
      if (
        attendanceMenuRef.current &&
        !attendanceMenuRef.current.contains(event.target)
      ) {
        setOpenAttendanceMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (show && shiftId) {
      loadShiftDetails();
    }
  }, [show, shiftId]);

  useEffect(() => {
    if (shift) {
      const fallbackSupervisorName =
        requestDefaults?.supervisorName ??
        requestDefaults?.supervisor ??
        requestDefaults?.supervisorLabel ??
        "";
      const fallbackSupervisorId =
        requestDefaults?.supervisorContactAccountId ??
        requestDefaults?.supervisor_account_id ??
        requestDefaults?.supervisor_contact_account_id ??
        null;
      setFormData({
        status: deriveModalStatusForForm(shift),
        shiftSupervisor:
          shift.supervisor_first_name && shift.supervisor_last_name
            ? `${shift.supervisor_first_name} ${shift.supervisor_last_name}`
            : shift.supervisor_name ||
              shift.supervisor ||
              fallbackSupervisorName,
        gender: shift.gender || "any",
        skills: shift.skills || [],
        certifications: shift.certifications || "",
        uniform: shift.uniform || "",
        noteToTempStaff: shift.note || shift.note_to_staff || "",
        adminNote: shift.admin_note || "",
        unpaidBreak: shift.unpaid_break > 0,
        staffNotification: shift.notify_staff_change === 1,
        travelTime: shiftRowHasTravel(
          shift.travel_time_amount,
          shift.travel_time_type,
        ),
        payRate: shift.pay_rate || "",
        billRate: shift.bill_rate || "",
        startDate: shift.start_date
          ? formatShiftInstantDisplay(shift.start_date)
          : "",
        endDate: shift.end_date
          ? formatShiftInstantDisplay(shift.end_date)
          : "",
        jobPostingId: "",
        travelTimeAmount:
          shift.travel_time_amount != null &&
          shift.travel_time_amount !== ""
            ? String(shift.travel_time_amount)
            : "",
        travelTimeType: shift.travel_time_type || "",
      });
      // initialize supervisorId for saving if present
      if (shift.supervisor_contact_account_id) {
        setSupervisorId(shift.supervisor_contact_account_id);
      } else if (shift.supervisor_account_id) {
        setSupervisorId(shift.supervisor_account_id);
      } else if (fallbackSupervisorId) {
        setSupervisorId(fallbackSupervisorId);
      } else {
        setSupervisorId(null);
      }
      // normalize skills into objects { id, name }
      const normalizedSkills = (shift.skills || shift.required_skills || [])
        .map((s) => {
          if (!s) return null;
          if (typeof s === "string" || typeof s === "number") {
            const raw = String(s).trim();
            return raw ? { id: raw, name: raw } : null;
          }
          // possible keys: skill_id, id, skillId, name, skill_name
          return {
            id: s.skill_id || s.id || s.skillId || null,
            name: s.name || s.skill_name || s.label || String(s),
          };
        })
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, skills: normalizedSkills }));

      // normalize certifications into objects { id, name }
      const normalizedCerts = (
        shift.certifications ||
        shift.required_certifications ||
        []
      )
        .map((c) => {
          if (!c) return null;
          if (typeof c === "string" || typeof c === "number") {
            const raw = String(c).trim();
            return raw ? { id: raw, name: raw } : null;
          }
          return {
            id: c.certification_id || c.id || c.certificationId || null,
            name: c.name || c.certification_name || c.label || String(c),
          };
        })
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, certifications: normalizedCerts }));
    }
  }, [shift, requestDefaults]);

  // Replace ID-only selected chips with human-readable names once options are loaded.
  useEffect(() => {
    if (!skillsOptions.length && !certOptions.length) return;

    setFormData((prev) => {
      const currentSkills = Array.isArray(prev.skills) ? prev.skills : [];
      const nextSkills = currentSkills.map((skill) => {
        if (!skill || typeof skill !== "object") return skill;
        const id = skill.id || skill.skill_id || skill.skillId || null;
        if (!id) return skill;
        const matched = skillsOptions.find(
          (opt) => String(opt.skill_id || opt.id) === String(id),
        );
        if (!matched?.name) return skill;
        const currentName = String(skill.name || "").trim();
        const isIdLikeName =
          !currentName ||
          currentName === String(id) ||
          /^\d+$/.test(currentName);
        if (!isIdLikeName) return skill;
        return { ...skill, id, name: matched.name };
      });

      const currentCerts = Array.isArray(prev.certifications)
        ? prev.certifications
        : [];
      const nextCerts = currentCerts.map((cert) => {
        if (!cert || typeof cert !== "object") return cert;
        const id =
          cert.id || cert.certification_id || cert.certificationId || null;
        if (!id) return cert;
        const matched = certOptions.find(
          (opt) => String(opt.certification_id || opt.id) === String(id),
        );
        if (!matched?.name) return cert;
        const currentName = String(cert.name || "").trim();
        const isIdLikeName =
          !currentName ||
          currentName === String(id) ||
          /^\d+$/.test(currentName);
        if (!isIdLikeName) return cert;
        return { ...cert, id, name: matched.name };
      });

      const skillsChanged = nextSkills.some((s, i) => s !== currentSkills[i]);
      const certsChanged = nextCerts.some((c, i) => c !== currentCerts[i]);
      if (!skillsChanged && !certsChanged) return prev;

      return {
        ...prev,
        skills: nextSkills,
        certifications: nextCerts,
      };
    });
  }, [skillsOptions, certOptions]);

  // Load supervisors, skills and certifications options when modal opens
  // also refresh when shift position/division changes
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      let positionSkills = [];
      let positionCerts = [];
      if (shift?.position_id) {
        try {
          let url = `/api/schedules/positions/${shift.position_id}/requirements`;
          if (shift.division_id) {
            url += `?divisionId=${shift.division_id}`;
          }
          const reqRes = await apiFetch(url);
          const reqJson = await reqRes.json().catch(() => ({}));
          positionSkills = Array.isArray(reqJson.skills) ? reqJson.skills : [];
          positionCerts = Array.isArray(reqJson.certifications)
            ? reqJson.certifications
            : [];
        } catch (e) {
          console.warn("Failed to load position requirements", e);
        }
      }

      let allSkills = [];
      let allCerts = [];
      try {
        const sRes = await apiFetch(`/api/personnel/skills?page=1&limit=1000`);
        const sJson = await sRes.json().catch(() => null);
        const sList = (sJson && (sJson.data || sJson)) || [];
        allSkills = Array.isArray(sList) ? sList : [];
      } catch (e) {
        console.warn("Failed to load skills", e);
      }
      try {
        const cRes = await apiFetch(
          `/api/personnel/certifications?page=1&limit=1000`,
        );
        const cJson = await cRes.json().catch(() => null);
        const cList = (cJson && (cJson.data || cJson)) || [];
        allCerts = Array.isArray(cList) ? cList : [];
      } catch (e) {
        console.warn("Failed to load certifications", e);
      }

      if (!cancelled) {
        setSkillsOptions(mergeSkillCertOptions(positionSkills, allSkills, "skill"));
        setCertOptions(
          mergeSkillCertOptions(positionCerts, allCerts, "certification"),
        );
      }
      try {
        // Load all active accounts for supervisors from dedicated endpoint
        const supRes = await apiFetch(`/api/personnel/supervisors`);
        const supJson = await supRes.json().catch(() => null);
        const supList = (supJson && (supJson.data || supJson)) || [];
        console.log("Loaded supervisors:", supList.length, supList.slice(0, 3));
        if (!cancelled)
          setSupervisorOptions(Array.isArray(supList) ? supList : []);
      } catch (e) {
        console.error("Failed to load supervisors", e);
        if (!cancelled) setSupervisorOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, shift?.position_id, shift?.division_id]);

  const loadShiftDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        credentials: "include",
      });
      const data = await response.json();
      const resolvedShift = data.data || data || {};
      const nextShift = { ...resolvedShift };
      const fallbackContactId =
        requestDefaults?.contactAccountId ??
        requestDefaults?.contact_account_id ??
        null;
      const fallbackClientId =
        requestDefaults?.clientId ??
        requestDefaults?.client_id ??
        requestDefaults?.client?.id ??
        requestDefaults?.client?.client_id ??
        null;
      const fallbackContactName =
        requestDefaults?.contactName ?? requestDefaults?.contact ?? "";
      const fallbackSupervisorId =
        requestDefaults?.supervisorContactAccountId ??
        requestDefaults?.supervisor_account_id ??
        requestDefaults?.supervisor_contact_account_id ??
        null;
      const fallbackSupervisorName =
        requestDefaults?.supervisorName ??
        requestDefaults?.supervisor ??
        requestDefaults?.supervisorLabel ??
        "";

      if (!nextShift.contact_account_id && fallbackContactId) {
        nextShift.contact_account_id = fallbackContactId;
      }
      if (!getShiftClientId(nextShift) && fallbackClientId) {
        nextShift.client_id = fallbackClientId;
      }
      if (!nextShift.contact_name && fallbackContactName) {
        nextShift.contact_name = fallbackContactName;
      }
      if (!nextShift.supervisor_contact_account_id && fallbackSupervisorId) {
        nextShift.supervisor_contact_account_id = fallbackSupervisorId;
      }
      if (!nextShift.supervisor_name && fallbackSupervisorName) {
        nextShift.supervisor_name = fallbackSupervisorName;
      }
      console.log("[manualAttendance] shift client id resolved", {
        shiftId,
        responseClientId: getShiftClientId(resolvedShift),
        fallbackClientId,
        resolvedClientId: getShiftClientId(nextShift),
      });

      setShift(nextShift);
    } catch (error) {
      console.error("Error loading shift details:", error);
    }
    setLoading(false);
  };

  const performSavePut = async (
    updateData,
    showToast = true,
    closeAfterSave = true,
  ) => {
    try {
      const response = await apiFetch(`/api/jobs/shifts/${shiftId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        console.log("Shift saved successfully");
        setShift((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            start_date: updateData.start_date ?? prev.start_date,
            end_date: updateData.end_date ?? prev.end_date,
            state: updateData.state ?? prev.state,
          };
          if (Object.prototype.hasOwnProperty.call(updateData, "note_to_staff")) {
            next.note = updateData.note_to_staff;
            next.note_to_staff = updateData.note_to_staff;
          }
          if (Object.prototype.hasOwnProperty.call(updateData, "admin_note")) {
            next.admin_note = updateData.admin_note;
          }
          return next;
        });
        if (showToast) showSuccess("Shift saved successfully!");
        if (typeof onRefresh === "function") onRefresh();
        if (closeAfterSave && typeof onHide === "function") {
          onHide();
        }
        return true;
      }
      const errorData = await response.text();
      console.error("Error updating shift:", errorData);
      let errorMessage = errorData;
      try {
        const parsed = JSON.parse(errorData);
        errorMessage = parsed?.message || errorData;
      } catch {
        // errorData wasn't JSON, use as-is
      }
      showError(errorMessage);
      return false;
    } catch (error) {
      console.error("Error saving shift:", error);
      showError("Error saving shift: " + error.message);
      return false;
    }
  };

  const submitUnassignModalAndSave = async () => {
    const pending = unassignPendingSaveRef.current;
    const m = unassignModalSnapshotRef.current;
    if (!pending?.updateData || !m.assignedAccountId || !shiftId) return;

    setUnassignSaveModal((s) => ({ ...s, submitting: true }));
    try {
      const reasonParts = [];
      if (m.reason) reasonParts.push(`Reason: ${m.reason}`);
      if (m.notice24h)
        reasonParts.push(
          `24h notice: ${m.notice24h === "yes" ? "Yes" : "No"}`,
        );
      if (m.availability) {
        const map = {
          available: "Available during shift",
          unavailable_shift: "Unavailable during shift",
          unavailable_day: "Unavailable all day",
          unavailable_range: "Unavailable date range",
        };
        reasonParts.push(
          `Availability: ${map[m.availability] || m.availability}`,
        );
      }
      if (m.note && m.note.trim()) reasonParts.push(`Note: ${m.note.trim()}`);
      const reasonStr = reasonParts.join(" | ");

      const resp = await apiFetch(`/api/shifts/${shiftId}/force-unassign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId: m.assignedAccountId,
          reason: reasonStr,
          affectsReliability: Boolean(m.countTowardsReliability),
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.success) {
        throw new Error(json.message || "Failed to unassign staff");
      }

      unassignPendingSaveRef.current = null;
      setUnassignSaveModal((s) => ({
        ...s,
        open: false,
        submitting: false,
      }));

      await loadShiftDetails();

      await performSavePut(
        pending.updateData,
        pending.showToast,
        pending.closeAfterSave,
      );
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to unassign staff");
      setUnassignSaveModal((s) => ({ ...s, submitting: false }));
    }
  };

  const handleSave = async (showToast = true, closeAfterSave = true) => {
    if (isClosedWeek) {
      showError(
        "This shift belongs to a closed accounting week and cannot be edited.",
      );
      return false;
    }
    try {
      if (
        String(formData.status).trim().toLowerCase() === "confirmed" &&
        !shiftHasAssignedStaffAccount(shift)
      ) {
        showError(
          "Cannot confirm appointment, no account is assigned. Please refresh your screen.",
          "Attention",
        );
        return false;
      }
      if (!formData.startDate?.trim() || !formData.endDate?.trim()) {
        showError("Start and end date/time are required.");
        return false;
      }
      const startDt = shiftDisplayToMysqlDateTime(formData.startDate);
      const endDt = shiftDisplayToMysqlDateTime(formData.endDate);
      if (!startDt || !endDt) {
        showError(
          "Enter valid start and end date/time (use the calendar or the same format as shown).",
        );
        return false;
      }

      const updateData = {
        state: formData.status,
        start_date: startDt,
        end_date: endDt,
        pay_rate: formData.payRate,
        bill_rate: formData.billRate,
        unpaid_break: formData.unpaidBreak ? 1 : 0,
        gender: formData.gender,
        admin_note: formData.adminNote,
        note_to_staff: formData.noteToTempStaff,
        division_id: shift.division_id,
        position_id: shift.position_id,
        travel_time_amount: formData.travelTime
          ? formData.travelTimeAmount !== "" && formData.travelTimeAmount != null
            ? formData.travelTimeAmount
            : shift?.client_travel_time_amount != null &&
                shift?.client_travel_time_amount !== ""
              ? shift.client_travel_time_amount
              : ""
          : "",
        travel_time_type: formData.travelTime
          ? (() => {
              const t = String(formData.travelTimeType || "").trim();
              if (t) return t;
              const m = normalizeShiftTravelType(
                shift?.client_travel_time_type,
              );
              if (m === "flat") return "flat_rate";
              if (m === "hourly") return "hourly";
              return "";
            })()
          : "",
        notify_staff_change: formData.staffNotification,
        uniform: formData.uniform,
      };
      if (supervisorId) updateData.supervisor_account_id = supervisorId;
      if (Array.isArray(formData.skills) && formData.skills.length > 0) {
        const skillIds = formData.skills
          .map((s) => s.id || s.skill_id || s.skillId || null)
          .filter(Boolean);
        updateData.required_skills = skillIds;
      } else {
        updateData.required_skills = [];
      }
      if (
        Array.isArray(formData.certifications) &&
        formData.certifications.length > 0
      ) {
        const certIds = formData.certifications
          .map((c) => c.id || c.certification_id || c.certificationId || null)
          .filter(Boolean);
        updateData.required_certifications = certIds;
      } else {
        updateData.required_certifications = [];
      }

      let assignedId = getShiftAssignedAccountId(shift);
      if (
        assignedId == null &&
        Array.isArray(shift?.assignments) &&
        shift.assignments.length > 0
      ) {
        const raw =
          shift.assignments[0]?.account_id ?? shift.assignments[0]?.accountId;
        assignedId = raw != null ? Number(raw) : null;
      }
      const needsUnassignFlow =
        isFormStatusRequiringUnassignFlow(formData.status) && assignedId != null;

      if (needsUnassignFlow) {
        unassignPendingSaveRef.current = {
          updateData,
          showToast,
          closeAfterSave,
        };
        const accountId = assignedId;
        const staffDisplayName =
          shift?.assigned_staff_name ||
          (shift?.first_name && shift?.last_name
            ? `${shift.first_name} ${shift.last_name}`.trim()
            : "") ||
          "Staff";
        setUnassignSaveModal({
          open: true,
          submitting: false,
          assignedAccountId: accountId,
          staffDisplayName,
          countTowardsReliability: true,
          reason: "Other",
          notice24h: "yes",
          availability: "available",
          note: "",
        });
        return false;
      }

      return await performSavePut(updateData, showToast, closeAfterSave)
        ? { saved: true, start_date: startDt, end_date: endDt }
        : false;
    } catch (error) {
      console.error("Error saving shift:", error);
      showError("Error saving shift: " + error.message);
      return false;
    }
  };

  const handleDuplicate = () => setShowDuplicateModal(true);

  const duplicateShiftPatch = React.useMemo(() => {
    if (!shift) return null;
    const skillIds = (formData.skills || [])
      .map((s) => s.id || s.skill_id || s.skillId)
      .filter(Boolean);
    const certIds = (
      Array.isArray(formData.certifications) ? formData.certifications : []
    )
      .map((c) => c.id || c.certification_id || c.certificationId)
      .filter(Boolean);
    const patch = {
      state: formData.status,
      division_id: shift.division_id,
      position_id: shift.position_id,
      pay_rate:
        formData.payRate !== "" && formData.payRate != null
          ? formData.payRate
          : shift.pay_rate,
      bill_rate:
        formData.billRate !== "" && formData.billRate != null
          ? formData.billRate
          : shift.bill_rate,
      unpaid_break: formData.unpaidBreak
        ? Number(shift.unpaid_break) > 0
          ? Number(shift.unpaid_break)
          : 30
        : 0,
      uniform: formData.uniform ?? shift.uniform,
      notify_staff_change: formData.staffNotification ? 1 : 0,
    };
    if (skillIds.length) patch.required_skills = skillIds;
    if (certIds.length) patch.required_certifications = certIds;
    return patch;
  }, [shift, formData]);

  if (!show) return null;

  const assignedAttendanceAccountId =
    shift?.assigned_account_id ||
    shift?.staff_account_id ||
    shift?.assigned_staff_id ||
    shift?.account_id ||
    null;

  const actualsAvatarStaff = shiftStaffEmployeeForAvatar(shift);

  const actualStateNorm = normalizeActualState(
    shift?.actualState ?? shift?.chosenActualState ?? shift?.actual_state ?? "",
  );
  const actualsSubmitted = isSubmittedActualState(
    shift?.actualState ?? shift?.chosenActualState ?? shift?.actual_state ?? "",
  );
  const signoffStateNorm = normalizeActualState(
    shift?.signoffState ?? shift?.signoff_state ?? "",
  );
  const timesheetReviewRequiredForShift =
    shift?.timesheet_review_required === 1 || shift?.timesheet_review_required === true;
  const checkInSignoffPendingReview =
    timesheetReviewRequiredForShift &&
    actualsSubmitted &&
    (signoffStateNorm === "pending_signoff_review" ||
      signoffStateNorm === "pendingsignoffreview");
  const checkInTimeBadgeClass = checkInSignoffPendingReview
    ? "pending-signoff-review"
    : actualsSubmitted
      ? "signoff-complete"
      : "pending";
  const checkInTimeBadgeLabel = checkInSignoffPendingReview
    ? "Pending signoff review"
    : actualsSubmitted
      ? "Signoff completed"
      : "Pending signoff";
  const signoffByContactName = String(
    shift?.contact_name ?? shift?.contactName ?? "",
  ).trim();
  const canSubmitActuals =
    actualsSubmitted || !!assignedAttendanceAccountId;
  const actualsDisplayLabel = actualsSubmitted
    ? actualStateLabel(actualStateNorm)
    : shift?.checkout_date && parseDbDateTime(shift.checkout_date)
      ? formatShiftInstantDisplay(shift.checkout_date, { compact: true })
      : "Not Submitted";
  const actualsActionLabel = actualsSubmitted ? "Modify" : "Submit Actuals";
  const actualsSubmittedCount = Math.max(
    0,
    Number(
      shift?.actuals_submitted_count ?? shift?.actualsSubmittedCount ?? 0,
    ) || 0,
  );

  const loadActualsHistory = async () => {
    if (!shift?.shift_id) return;
    setActualsHistoryLoading(true);
    try {
      const response = await apiFetch(
        `/api/jobs/shifts/${shift.shift_id}/actuals`,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load actuals history");
      }
      setActualsHistory(Array.isArray(data?.data?.actuals) ? data.data.actuals : []);
      const count = data?.data?.count;
      if (typeof count === "number" && count >= 0) {
        setShift((prev) =>
          prev
            ? {
                ...prev,
                actuals_submitted_count: count,
                actualsSubmittedCount: count,
              }
            : prev,
        );
      }
    } catch (err) {
      console.error("Load actuals history failed:", err);
      showError(err?.message || "Failed to load actuals history");
    } finally {
      setActualsHistoryLoading(false);
    }
  };

  const openActualsHistoryModal = async () => {
    setShowActualsHistoryModal(true);
    setHistoryRowMenuId(null);
    await loadActualsHistory();
  };

  const handleSetActiveActual = async (shiftActualId) => {
    if (
      isClosedWeek ||
      !canWriteShiftActual ||
      !shift?.shift_id ||
      settingActiveActualId
    ) {
      return;
    }
    setSettingActiveActualId(shiftActualId);
    setHistoryRowMenuId(null);
    try {
      const response = await apiFetch(
        `/api/jobs/shifts/${shift.shift_id}/actuals/active`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shiftActualId }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to set active actual");
      }
      const updatedShift = data?.data?.shift;
      if (updatedShift) {
        setShift((prev) =>
          prev
            ? {
                ...prev,
                ...updatedShift,
                actuals_submitted_count:
                  prev.actuals_submitted_count ?? prev.actualsSubmittedCount,
                actualsSubmittedCount:
                  prev.actualsSubmittedCount ?? prev.actuals_submitted_count,
              }
            : prev,
        );
      } else {
        await loadShiftDetails();
      }
      await loadActualsHistory();
      if (typeof onRefresh === "function") onRefresh();
      showSuccess("Active actual updated");
    } catch (err) {
      console.error("Set active actual failed:", err);
      showError(err?.message || "Failed to set active actual");
    } finally {
      setSettingActiveActualId(null);
    }
  };

  const manualAttendanceAction =
    shift?.checkin_date && !shift?.checkout_date
      ? { mode: "checkout", label: "Check out" }
      : { mode: "checkin", label: "Check in" };

  const openManualAttendanceModal = (mode) => {
    setManualAttendanceMode(mode);
    setOpenAttendanceMenu(null);
    setShowManualCheckIn(true);
  };

  const openActualsSubmissionModal = () => {
    if (!canWriteShiftActual) return;
    const initialState =
      actualsSubmitted && actualStateNorm !== "notsubmitted"
        ? actualStateNorm
        : "as_scheduled";
    setActualsForm({
      actualState: initialState,
      actualStartDate: toDateTimeLocalValue(
        shift?.actualStartDate ||
          shift?.actualStartDateIso ||
          shift?.actual_start_date ||
          shift?.checkin_date ||
          shift?.start_date,
      ),
      actualEndDate: toDateTimeLocalValue(
        shift?.actualEndDate ||
          shift?.actualEndDateIso ||
          shift?.actual_end_date ||
          shift?.checkout_date ||
          shift?.end_date,
      ),
      actualUnpaidBreakMinutes:
        shift?.actualUnpaidBreakMinutes ??
        shift?.actual_unpaid_break_minutes ??
        (shift?.unpaid_break === true || shift?.unpaid_break === 1 || shift?.unpaid_break === "1" ? 30 : (shift?.unpaid_break ?? 0)),
      note: shift?.actualNote || "",
    });
    setOpenAttendanceMenu(null);
    setShowActualsModal(true);
  };

  const handleActualsFormChange = (field, value) => {
    setActualsForm((prev) => {
      if (
        field === "actualEndDate" &&
        prev.actualStartDate &&
        value &&
        value < prev.actualStartDate
      ) {
        return { ...prev, actualEndDate: prev.actualStartDate };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmitActuals = async () => {
    if (!canWriteShiftActual || !shift?.shift_id || actualsSubmitting) return;

    const payload = {
      actualState: actualsForm.actualState,
      actualStartDate:
        actualsForm.actualState === "different"
          ? toMysqlUtcWallString(fromDatetimeLocalValue(actualsForm.actualStartDate))
          : toMysqlUtcWallString(
              parseDbDateTime(shift?.start_date) ||
                shift?.start_date,
            ),
      actualEndDate:
        actualsForm.actualState === "different"
          ? toMysqlUtcWallString(fromDatetimeLocalValue(actualsForm.actualEndDate))
          : toMysqlUtcWallString(
              parseDbDateTime(shift?.end_date) || shift?.end_date,
            ),
      actualUnpaidBreakMinutes:
        actualsForm.actualState === "different"
          ? actualsForm.actualUnpaidBreakMinutes
          : (shift?.unpaid_break === true || shift?.unpaid_break === 1 || shift?.unpaid_break === "1" ? 30 : (shift?.unpaid_break ?? 0)),
      note: actualsForm.note || "",
      accountId: assignedAttendanceAccountId,
    };

    if (
      actualsForm.actualState === "different" &&
      (!payload.actualStartDate || !payload.actualEndDate)
    ) {
      showError("Select actual start and end times");
      return;
    }
    if (actualsForm.actualState === "different") {
      if (actualsForm.actualEndDate < actualsForm.actualStartDate) {
        showError("Actual end cannot be before actual start");
        return;
      }
    }

    setActualsSubmitting(true);
    try {
      const response = await apiFetch(`/api/jobs/shifts/${shift.shift_id}/actuals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to submit actuals");
      }

      const updatedShift = data?.data?.shift || null;
      if (updatedShift) {
        setShift((prev) =>
          prev
            ? {
                ...prev,
                ...updatedShift,
                assignment_ui_status: "confirmed",
                assignmentUiStatus: "confirmed",
                actuals_submitted_count: Math.max(
                  Number(prev.actuals_submitted_count ?? prev.actualsSubmittedCount ?? 0) + 1,
                  1,
                ),
                actualsSubmittedCount: Math.max(
                  Number(prev.actualsSubmittedCount ?? prev.actuals_submitted_count ?? 0) + 1,
                  1,
                ),
              }
            : prev,
        );
      } else {
        await loadShiftDetails();
      }
      setFormData((prev) => ({ ...prev, status: "confirmed" }));
      if (typeof onRefresh === "function") onRefresh();
      setShowActualsModal(false);
      showSuccess(
        `Actuals submitted: ${actualStateLabel(actualsForm.actualState)}`,
      );
    } catch (err) {
      console.error("Submit actuals failed:", err);
      showError(err?.message || "Failed to submit actuals");
    } finally {
      setActualsSubmitting(false);
    }
  };

  const handleManualAttendance = async (date) => {
    if (manualAttendanceSaving) return;
    if (!date) {
      showError(
        `Please select a ${manualAttendanceMode === "checkout" ? "check-out" : "check-in"} date`,
      );
      return;
    }
    if (!assignedAttendanceAccountId) {
      showError("No assigned staff found for this shift");
      return;
    }

    setManualAttendanceSaving(true);
    try {
      const endpoint =
        manualAttendanceMode === "checkout" ? "checkout" : "checkin";
      const roundedTimestamp = await roundManualAttendanceTimestamp(
        date,
        manualAttendanceMode === "checkout" ? "checkout" : "checkin",
        shift,
      );
      const res = await apiFetch(`/api/shifts/${shift.shift_id}/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          timestamp: roundedTimestamp,
          method: "manual",
          accountId: assignedAttendanceAccountId,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        showSuccess(
          manualAttendanceMode === "checkout"
            ? "Manual check-out saved"
            : "Manual check-in saved",
        );
        setShowManualCheckIn(false);

        // reload shift data
        await loadShiftDetails();
      } else {
        showError(
          data.message ||
            (manualAttendanceMode === "checkout"
              ? "Check-out failed"
              : "Check-in failed"),
        );
      }
    } catch (err) {
      console.error(err);
      showError(
        manualAttendanceMode === "checkout"
          ? "Server error while saving check-out"
          : "Server error while saving check-in",
      );
    } finally {
      setManualAttendanceSaving(false);
    }
  };

  const shiftDatePickerMin =
    !datetimePickerTarget || datetimePickerTarget === "start"
      ? null
      : shiftDisplayToDate(formData.startDate);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center ${overlayZClass} p-4 md:p-10 overflow-y-auto`}
        onClick={onHide}
      >
        <div
          className="bg-white w-full max-w-3xl rounded-lg shadow-lg overflow-hidden shift-detail-modal flex flex-col h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="shift-header">
            <h1 className="shift-title">Shift {shift?.shift_id || shiftId}</h1>
            <p className="shift-subtitle">
              {shift?.client_name || "Client"} -{" "}
              {shift?.location_name || "Location"} -{" "}
              {shift?.division_name || "Division"} -{" "}
              {shift?.position_name || "Position"}
            </p>
          </header>
          {isClosedWeek && (
            <div className="mx-6 mt-4 rounded bg-[#f36f61] px-4 py-3 text-base font-semibold text-white">
              ! Week is closed, modifications are disabled
            </div>
          )}

          <div className="shift-content flex flex-1 overflow-hidden">
            <aside className="shift-sidebar">
              <button
                className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`tab-btn ${activeTab === "audits" ? "active" : ""}`}
                onClick={() => setActiveTab("audits")}
              >
                Audits
              </button>
            </aside>

            <main className="shift-main flex-1 overflow-y-auto">
              {activeTab === "overview" ? (
                <div className="p-6 pt-0">
                  <fieldset
                    className="border-0 m-0 p-0 min-w-0"
                  >
                    <div className={isClosedWeek ? "shift-overview-locked" : ""}>
                    <div className="section-header">
                      <h2 className="section-title">Overview</h2>
                      <button
                        className="modify-btn"
                        onClick={() => setShowDivisionModal(true)}
                      >
                        MODIFY DIVISION AND POSITION
                      </button>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Status</label>
                      <select
                        className="form-select"
                        value={formData.status || "unassigned"}
                        onChange={(e) =>
                          handleInputChange("status", e.target.value)
                        }
                      >
                        {/* include full set of states used in filters plus the original ones */}
                        <option value="unassigned">Unassigned</option>
                        <option value="tentative">Tentative</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="declined">Unfilled</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                    </div>

                    <div
                      className={
                        isClosedWeek ? "shift-closed-week-readonly" : ""
                      }
                    >
                    <div className="form-group">
                      <label className="form-label">Assigned to</label>
                      <div className="flex flex-col">
                        {(() => {
                          const displayName =
                            shift?.assigned_staff_name ||
                            shift?.staffDisplay ||
                            (shift?.first_name && shift?.last_name
                              ? `${shift.first_name} ${shift.last_name}`
                              : "");
                          const payerSuffix =
                            shift?.staffPayrollPayer && displayName
                              ? ` (${shift.staffPayrollPayer})`
                              : "";
                          const accountId = shift?.assigned_staff_id;
                          if (displayName && accountId && onOpenPersonnelModal) {
                            return (
                              <span>
                                <span
                                  className="text-red-500 cursor-pointer hover:text-red-600 hover:underline"
                                  onClick={() =>
                                    onOpenPersonnelModal({
                                      id: accountId,
                                      account_id: accountId,
                                      name: displayName,
                                      type: "Field Staff",
                                    })
                                  }
                                >
                                  {displayName}
                                </span>
                                {payerSuffix}
                              </span>
                            );
                          }
                          return (
                            <span className="text-gray-700">
                              {displayName || "Unassigned"}
                              {payerSuffix}
                            </span>
                          );
                        })()}
                        {shift?.assigned_staff_phone && (
                          <span className="text-sm text-gray-500">
                            {shift.assigned_staff_phone}
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          Pay Rate: $
                          {formData.payRate || shift?.pay_rate || "0.00"}
                        </span>
                        {(() => {
                          if (!shiftHasAssignedStaffAccount(shift)) return null;
                          const carpoolName = String(
                            shift?.carpool_name || "",
                          ).trim();
                          const carpoolNote = String(
                            shift?.carpool_note || "",
                          ).trim();
                          const carpoolLine =
                            carpoolName && carpoolNote
                              ? `${carpoolName} • ${carpoolNote}`
                              : carpoolName || carpoolNote;
                          const hasCarpool = !!(carpoolName || carpoolNote);
                          const ignoreAvail =
                            Number(shift?.ignore_availability) === 1;
                          const carTitleParts = [];
                          if (carpoolName) carTitleParts.push(carpoolName);
                          if (carpoolName && carpoolNote)
                            carTitleParts.push("•");
                          if (carpoolNote) carTitleParts.push(carpoolNote);
                          const carTitle =
                            carTitleParts.join(" ").trim() || "Carpool";
                          return (
                            <div
                              className="text-xs mt-1 grid w-full min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-start gap-x-1.5 gap-y-0.5"
                              title={carTitle}
                            >
                              <div className="flex shrink-0 justify-center pt-0.5">
                                {ignoreAvail ? (
                                  <span
                                    title="Availability & requirements being ignored"
                                    className="inline-flex"
                                    role="img"
                                    aria-label="Availability and requirements being ignored"
                                  >
                                    <MdDoNotDisturbOff
                                      className="text-gray-500"
                                      size={14}
                                    />
                                  </span>
                                ) : null}
                              </div>
                              {isClosedWeek ? (
                                <span
                                  title="Staff requires notification of change."
                                  className="inline-flex shrink-0 pt-0.5"
                                  role="img"
                                  aria-label="Staff requires notification of change"
                                >
                                  <FaFlag
                                    size={12}
                                    className={
                                      formData.staffNotification
                                        ? "text-red-600"
                                        : "text-gray-500"
                                    }
                                  />
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  title="Staff requires notification of change."
                                  onClick={handleAssignedRowNotifyToggle}
                                  disabled={notifyFlagSaving}
                                  aria-pressed={formData.staffNotification}
                                  className="inline-flex shrink-0 border-0 bg-transparent p-0 pt-0.5 cursor-pointer disabled:opacity-50"
                                >
                                  <FaFlag
                                    size={12}
                                    className={
                                      formData.staffNotification
                                        ? "text-red-600"
                                        : "text-gray-500"
                                    }
                                  />
                                </button>
                              )}
                              {isClosedWeek ? (
                                <div
                                  className="flex min-w-0 w-full items-start gap-1 text-left"
                                  title={carTitle}
                                >
                                  <FaCar
                                    size={12}
                                    className={
                                      hasCarpool
                                        ? "text-red-600 shrink-0 mt-0.5"
                                        : "text-gray-500 shrink-0 mt-0.5"
                                    }
                                  />
                                  {hasCarpool ? (
                                    <span className="min-w-0 flex-1 text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                                      {carpoolLine}
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  title={carTitle}
                                  onClick={() =>
                                    setCarpoolViewer({
                                      open: true,
                                      name: carpoolName,
                                      note: carpoolNote,
                                    })
                                  }
                                  className="flex min-w-0 w-full items-start gap-1 border-0 bg-transparent p-0 m-0 cursor-pointer text-left"
                                >
                                  <FaCar
                                    size={12}
                                    className={
                                      hasCarpool
                                        ? "text-red-600 shrink-0 mt-0.5"
                                        : "text-gray-500 shrink-0 mt-0.5"
                                    }
                                  />
                                  {hasCarpool ? (
                                    <span className="min-w-0 flex-1 text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                                      {carpoolLine}
                                    </span>
                                  ) : null}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="status-row">
                      <div className="status-col">
                        <label className="form-label">Check in time</label>
                        <div style={{ display: "flex", alignItems: "flex-start" }}>
                          {(() => {
                            if (checkInSignoffPendingReview) {
                              return (
                                <span
                                  className={`status-badge ${checkInTimeBadgeClass}`}
                                >
                                  {checkInTimeBadgeLabel}
                                </span>
                              );
                            }
                            if (actualsSubmitted) {
                              return (
                                <span className="status-badge signoff-complete">
                                  Signoff completed
                                </span>
                              );
                            }

                            if (shift?.checkout_date && hasAppDateTime(shift.checkout_date)) {
                              return (
                                <div className="check-in-time-stack">
                                  <div className="check-in-time-line">
                                    {formatCheckInTimeLine(
                                      shift.checkin_date,
                                      shift.checkout_date,
                                    )}
                                  </div>
                                  <span className="status-badge pending">
                                    Pending signoff
                                  </span>
                                </div>
                              );
                            }

                            if (shift?.checkin_date && hasAppDateTime(shift.checkin_date)) {
                              return (
                                <div className="check-in-time-stack">
                                  <div className="check-in-time-line">
                                    {formatCheckInTimeLine(shift.checkin_date, null)}
                                  </div>
                                  <span className="status-badge checked-in">
                                    Checked in
                                  </span>
                                </div>
                              );
                            }

                            const now = new Date();
                            if (shift && shift.start_date) {
                              const start = parseDbDateTime(shift.start_date);
                              if (start && start > now) {
                                return (
                                  <span className="status-badge future">
                                    Future
                                  </span>
                                );
                              }

                              return (
                                <span className="status-badge late">Late</span>
                              );
                            }

                            return (
                              <span className="status-badge pending">
                                Pending signoff
                              </span>
                            );
                          })()}
                          {!(
                            shift?.checkout_date &&
                            shift?.checkout_date && hasAppDateTime(shift.checkout_date)
                          ) &&
                          canWriteShiftActual && (
                          <div
                            className="attendance-menu-wrapper"
                            ref={
                              openAttendanceMenu === "checkin"
                                ? attendanceMenuRef
                                : null
                            }
                          >
                            <span
                              className="menu-dots"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenAttendanceMenu((prev) =>
                                  prev === "checkin" ? null : "checkin",
                                );
                              }}
                            >
                              ⋮
                            </span>
                            {openAttendanceMenu === "checkin" && (
                              <button
                                type="button"
                                className="attendance-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openManualAttendanceModal(
                                    manualAttendanceAction.mode,
                                  );
                                }}
                              >
                                {manualAttendanceAction.label}
                              </button>
                            )}
                          </div>
                          )}
                        </div>
                        {checkInSignoffPendingReview && signoffByContactName ? (
                          <div className="text-xs text-gray-600 mt-1">
                            Signed off by {signoffByContactName}
                          </div>
                        ) : null}
                      </div>
                      <div className="status-col">
                        <label className="form-label">Actual Time</label>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span
                            className={`status-badge ${actualsSubmitted ? "actual-submitted" : "not-submitted"}`}
                          >
                            {actualsDisplayLabel}
                          </span>
                          {canSubmitActuals && canWriteShiftActual && !isClosedWeek && (
                          <div
                            className="attendance-menu-wrapper"
                            ref={
                              openAttendanceMenu === "actuals"
                                ? attendanceMenuRef
                                : null
                            }
                          >
                            <span
                              className="menu-dots"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenAttendanceMenu((prev) =>
                                  prev === "actuals" ? null : "actuals",
                                );
                              }}
                            >
                              ⋮
                            </span>
                            {openAttendanceMenu === "actuals" && (
                              <button
                                type="button"
                                className="attendance-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openActualsSubmissionModal();
                                }}
                              >
                                {actualsActionLabel}
                              </button>
                            )}
                          </div>
                          )}
                        </div>
                        {actualsSubmittedCount > 0 ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="actuals-history-link"
                            onClick={openActualsHistoryModal}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openActualsHistoryModal();
                              }
                            }}
                          >
                            {actualsSubmittedCount === 1
                              ? "1 Actual Submitted"
                              : `${actualsSubmittedCount} Actuals Submitted`}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    </div>

                    <div className={isClosedWeek ? "shift-overview-locked" : ""}>

                    <div className="form-group">
                      <div className="datetime-row">
                        <div style={{ flex: 1 }}>
                          <label className="form-label required">Start</label>
                          <div className="datetime-field">
                            <button
                              type="button"
                              className="datetime-calendar-btn"
                              disabled={isClosedWeek}
                              aria-label="Open start date and time"
                              onClick={() =>
                                !isClosedWeek && openShiftDatePicker("start")
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  ry="2"
                                />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            </button>
                            <input
                              type="text"
                              className="datetime-input"
                              readOnly={isClosedWeek}
                              autoComplete="off"
                              placeholder="Apr 19, 2026 11:40 AM"
                              value={formData.startDate || ""}
                              onChange={(e) =>
                                handleInputChange("startDate", e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="clear-btn"
                              disabled={isClosedWeek}
                              aria-label="Clear start"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isClosedWeek)
                                  handleInputChange("startDate", "");
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <span className="datetime-separator">to</span>
                        <div style={{ flex: 1 }}>
                          <label className="form-label required">End</label>
                          <div className="datetime-field">
                            <button
                              type="button"
                              className="datetime-calendar-btn"
                              disabled={isClosedWeek}
                              aria-label="Open end date and time"
                              onClick={() =>
                                !isClosedWeek && openShiftDatePicker("end")
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  ry="2"
                                />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            </button>
                            <input
                              type="text"
                              className="datetime-input"
                              readOnly={isClosedWeek}
                              autoComplete="off"
                              placeholder="Apr 19, 2026 4:00 PM"
                              value={formData.endDate || ""}
                              onChange={(e) =>
                                handleInputChange("endDate", e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="clear-btn"
                              disabled={isClosedWeek}
                              aria-label="Clear end"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isClosedWeek)
                                  handleInputChange("endDate", "");
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">Pay Rate</label>
                        <div className="field-with-clear">
                          <span
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            $
                          </span>
                          <input
                            type="text"
                            value={formData.payRate || ""}
                            onChange={(e) =>
                              handleInputChange("payRate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label required">Bill Rate</label>
                        <div className="field-with-clear">
                          <span
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            $
                          </span>
                          <input
                            type="text"
                            value={formData.billRate || ""}
                            onChange={(e) =>
                              handleInputChange("billRate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="checkbox-group">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={formData.unpaidBreak || false}
                          onChange={(e) =>
                            handleInputChange("unpaidBreak", e.target.checked)
                          }
                        />
                        <span className="checkbox-label">
                          Unpaid 30 Minute Break
                        </span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={formData.staffNotification || false}
                          onChange={(e) =>
                            handleInputChange(
                              "staffNotification",
                              e.target.checked,
                            )
                          }
                        />
                        <span className="checkbox-label">
                          Staff Requires Notification of Change
                        </span>
                      </label>
                      {showTravelTimeCheckbox && (
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            checked={formData.travelTime || false}
                            onChange={(e) =>
                              applyTravelCheckbox(e.target.checked)
                            }
                          />
                          <span className="checkbox-label">
                            {travelTimeCheckboxLabel}
                          </span>
                        </label>
                      )}
                    </div>

                    <div
                      className="form-group"
                      ref={supervisorRef}
                      style={{ position: "relative" }}
                    >
                      <label className="form-label">Shift Supervisor</label>
                      <div
                        className="custom-select-wrapper"
                        onClick={() => {
                          console.log(
                            "Supervisor dropdown clicked, current open state:",
                            supervisorDropdownOpen,
                          );
                          console.log(
                            "Supervisor options available:",
                            supervisorOptions.length,
                          );
                          setSupervisorDropdownOpen(!supervisorDropdownOpen);
                        }}
                        style={{
                          border: "1px solid hsl(var(--input))",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          minHeight: "42px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "hsl(var(--background))",
                        }}
                      >
                        <span
                          style={{
                            color: formData.shiftSupervisor
                              ? "inherit"
                              : "hsl(var(--muted-foreground))",
                          }}
                        >
                          {formData.shiftSupervisor || "Select supervisor"}
                        </span>
                        <span style={{ marginLeft: "auto", fontSize: "12px" }}>
                          ▼
                        </span>
                      </div>
                      {supervisorDropdownOpen && (
                        <div
                          className="custom-dropdown"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: "white",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            marginTop: "4px",
                            maxHeight: "240px",
                            overflow: "auto",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div style={{ padding: "8px" }}>
                            <input
                              type="text"
                              placeholder="Search supervisor..."
                              value={supervisorQuery}
                              onChange={(e) => {
                                setSupervisorQuery(e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="form-control"
                              style={{ marginBottom: "8px" }}
                            />
                          </div>
                          {supervisorOptions.length === 0 && (
                            <div
                              style={{
                                padding: "12px",
                                color: "hsl(var(--muted-foreground))",
                                textAlign: "center",
                              }}
                            >
                              No supervisors found
                            </div>
                          )}
                          {supervisorOptions
                            .filter((s) => {
                              if (!supervisorQuery) return true;
                              const name =
                                s.name ||
                                `${s.first_name || ""} ${s.last_name || ""}`.trim();
                              return name
                                .toLowerCase()
                                .includes(supervisorQuery.toLowerCase());
                            })
                            .map((s) => (
                              <div
                                key={s.account_id || s.id}
                                style={{
                                  padding: "10px 12px",
                                  cursor: "pointer",
                                  borderTop: "1px solid hsl(var(--border))",
                                  background:
                                    supervisorId === (s.account_id || s.id)
                                      ? "hsl(var(--accent))"
                                      : "transparent",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.background =
                                    "hsl(var(--accent))")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.background =
                                    supervisorId === (s.account_id || s.id)
                                      ? "hsl(var(--accent))"
                                      : "transparent")
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const name =
                                    s.name ||
                                    `${s.first_name || ""} ${s.last_name || ""}`.trim();
                                  console.log(
                                    "Selected supervisor:",
                                    name,
                                    s.account_id || s.id,
                                  );
                                  setSupervisorId(s.account_id || s.id);
                                  handleInputChange("shiftSupervisor", name);
                                  setSupervisorDropdownOpen(false);
                                  setSupervisorQuery("");
                                }}
                              >
                                {s.name ||
                                  `${s.first_name || ""} ${s.last_name || ""}`.trim()}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    <h3 className="requirements-title">Requirements</h3>

                    <div className="form-group">
                      <label className="form-label">Gender</label>
                      <select
                        className="form-select"
                        value={formData.gender || "any"}
                        onChange={(e) =>
                          handleInputChange("gender", e.target.value)
                        }
                      >
                        <option value="any">Any</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>

                    <div
                      className="form-group"
                      ref={skillsRef}
                      style={{ position: "relative" }}
                    >
                      <label className="form-label">Skills</label>
                      <div
                        className="custom-select-wrapper"
                        onClick={() =>
                          setSkillsDropdownOpen(!skillsDropdownOpen)
                        }
                        style={{
                          border: "1px solid hsl(var(--input))",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          minHeight: "42px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                          alignItems: "center",
                          background: "hsl(var(--background))",
                        }}
                      >
                        {(formData.skills || []).length === 0 && (
                          <span
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            Select skills
                          </span>
                        )}
                        {(formData.skills || []).map((skill, idx) => (
                          <span
                            key={idx}
                            className="skill-chip"
                            style={{
                              background: "hsl(var(--accent))",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "13px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {resolveSkillChipLabel(skill, skillsOptions)}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSkill(skill.name || skill);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontSize: "16px",
                                lineHeight: 1,
                                padding: 0,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <span style={{ marginLeft: "auto", fontSize: "12px" }}>
                          ▼
                        </span>
                      </div>
                      {skillsDropdownOpen && (
                        <div
                          className="custom-dropdown"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: "white",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            marginTop: "4px",
                            maxHeight: "240px",
                            overflow: "auto",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div style={{ padding: "8px" }}>
                            <input
                              type="text"
                              placeholder="Search skills..."
                              value={skillSearch}
                              onChange={(e) => setSkillSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="form-control"
                              style={{ marginBottom: "8px" }}
                            />
                          </div>
                          {skillsOptions
                            .filter((s) => {
                              if (!skillSearch) return true;
                              return s.name
                                .toLowerCase()
                                .includes(skillSearch.toLowerCase());
                            })
                            .map((s) => {
                              const isSelected = (formData.skills || []).some(
                                (skill) =>
                                  (skill.id || skill.skill_id) ===
                                  (s.skill_id || s.id),
                              );
                              return (
                                <div
                                  key={s.skill_id || s.id}
                                  style={{
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    borderTop: "1px solid hsl(var(--border))",
                                    background: isSelected
                                      ? "hsl(var(--accent))"
                                      : "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.background =
                                      "hsl(var(--accent))")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.background =
                                      isSelected
                                        ? "hsl(var(--accent))"
                                        : "transparent")
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const item = {
                                      id: s.skill_id || s.id,
                                      name: s.name,
                                    };
                                    const exists = (formData.skills || []).some(
                                      (skill) =>
                                        (skill.id || skill.skill_id) ===
                                        item.id,
                                    );
                                    if (!exists) {
                                      setFormData((prev) => ({
                                        ...prev,
                                        skills: [...(prev.skills || []), item],
                                      }));
                                      setShift((prev) => ({
                                        ...prev,
                                        skills: [...(prev.skills || []), item],
                                      }));
                                    }
                                    setSkillSearch("");
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    readOnly
                                    style={{ pointerEvents: "none" }}
                                  />
                                  {s.name}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    <div
                      className="form-group"
                      ref={certsRef}
                      style={{ position: "relative" }}
                    >
                      <label className="form-label">Certifications</label>
                      <div
                        className="custom-select-wrapper"
                        onClick={() => setCertsDropdownOpen(!certsDropdownOpen)}
                        style={{
                          border: "1px solid hsl(var(--input))",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          minHeight: "42px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                          alignItems: "center",
                          background: "hsl(var(--background))",
                        }}
                      >
                        {(Array.isArray(formData.certifications)
                          ? formData.certifications
                          : []
                        ).length === 0 && (
                          <span
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            Select certifications
                          </span>
                        )}
                        {(Array.isArray(formData.certifications)
                          ? formData.certifications
                          : []
                        ).map((cert, idx) => (
                          <span
                            key={idx}
                            className="cert-chip"
                            style={{
                              background: "hsl(var(--accent))",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "13px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {resolveCertChipLabel(cert, certOptions)}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCert(cert);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontSize: "16px",
                                lineHeight: 1,
                                padding: 0,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <span style={{ marginLeft: "auto", fontSize: "12px" }}>
                          ▼
                        </span>
                      </div>
                      {certsDropdownOpen && (
                        <div
                          className="custom-dropdown"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: "white",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            marginTop: "4px",
                            maxHeight: "240px",
                            overflow: "auto",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div style={{ padding: "8px" }}>
                            <input
                              type="text"
                              placeholder="Search certifications..."
                              value={certSearch}
                              onChange={(e) => setCertSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="form-control"
                              style={{ marginBottom: "8px" }}
                            />
                          </div>
                          {certOptions
                            .filter((c) => {
                              if (!certSearch) return true;
                              return c.name
                                .toLowerCase()
                                .includes(certSearch.toLowerCase());
                            })
                            .map((c) => {
                              const isSelected = (
                                Array.isArray(formData.certifications)
                                  ? formData.certifications
                                  : []
                              ).some(
                                (cert) =>
                                  (cert.id || cert.certification_id) ===
                                  (c.certification_id || c.id),
                              );
                              return (
                                <div
                                  key={c.certification_id || c.id}
                                  style={{
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    borderTop: "1px solid hsl(var(--border))",
                                    background: isSelected
                                      ? "hsl(var(--accent))"
                                      : "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.background =
                                      "hsl(var(--accent))")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.background =
                                      isSelected
                                        ? "hsl(var(--accent))"
                                        : "transparent")
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const item = {
                                      id: c.certification_id || c.id,
                                      name: c.name,
                                    };
                                    const currentCerts = Array.isArray(
                                      formData.certifications,
                                    )
                                      ? formData.certifications
                                      : [];
                                    const exists = currentCerts.some(
                                      (cert) =>
                                        (cert.id || cert.certification_id) ===
                                        item.id,
                                    );
                                    if (!exists) {
                                      setFormData((prev) => ({
                                        ...prev,
                                        certifications: [...currentCerts, item],
                                      }));
                                      setShift((prev) => ({
                                        ...prev,
                                        certifications: [
                                          ...(Array.isArray(prev.certifications)
                                            ? prev.certifications
                                            : []),
                                          item,
                                        ],
                                      }));
                                    }
                                    setCertSearch("");
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    readOnly
                                    style={{ pointerEvents: "none" }}
                                  />
                                  {c.name}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Uniform</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.uniform || ""}
                        onChange={(e) =>
                          handleInputChange("uniform", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Note to Temp Staff</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.noteToTempStaff || ""}
                        onChange={(e) =>
                          handleInputChange("noteToTempStaff", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Admin Note</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.adminNote || ""}
                        onChange={(e) =>
                          handleInputChange("adminNote", e.target.value)
                        }
                      />
                    </div>
                    </div>
                  </fieldset>
                </div>
              ) : (
                <div className="p-4 pt-0">
                  <RichAuditsPanel
                    subjectId={shiftId}
                    fetchUrl={
                      shiftId
                        ? `${BASE_URL}/api/jobs/shifts/${shiftId}/audits/rich`
                        : ""
                    }
                    title="Audit History"
                  />
                </div>
              )}
            </main>
          </div>

          {/* Fixed Footer */}
          <footer className="shift-footer">
            {!isClosedWeek && (
              <button className="footer-btn primary" onClick={handleSave}>
                SAVE
              </button>
            )}
            {!isClosedWeek && (
              <button className="footer-btn" onClick={handleDuplicate}>
                SAVE & DUPLICATE SHIFT
              </button>
            )}
            <button
              className="footer-btn"
              onClick={() => {
                try {
                  let date = null;
                  if (shift && shift.start_date) {
                    date = toAppDateKey(shift.start_date);
                  }
                  const id = (shift && (shift.shift_id || shift.id)) || shiftId;
                  if (date && id) {
                    window.location.href = `/schedule?date=${encodeURIComponent(
                      date,
                    )}&shiftId=${encodeURIComponent(id)}`;
                  } else if (id) {
                    window.location.href = `/schedule?shiftId=${encodeURIComponent(
                      id,
                    )}`;
                  } else {
                    window.location.href = `/schedule`;
                  }
                } catch {
                  window.location.href = `/schedule`;
                }
              }}
            >
              SCHEDULER VIEW
            </button>
            <button className="footer-btn" onClick={onHide}>
              CANCEL
            </button>
          </footer>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${nestedOverlayZClass} flex items-center justify-center`}
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <div className="text-lg font-semibold">Assignment Details</div>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✖
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Overview
                  </div>
                  <div className="text-base">Cleaner</div>
                  <div className="text-sm text-gray-600">
                    200 Coventry Road, Ottawa, On K1K 4S3
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Schedule
                  </div>
                  <div className="text-base">
                    Oct 29 · 4:30 pm – Oct 30, 1 am
                  </div>
                  <div className="text-sm text-gray-600">8h 30m</div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Uniform
                  </div>
                  <div className="text-sm">
                    Black pants - Black shirt - Black socks - Black non-slip/oil
                    resistant shoes
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Pay Rate
                  </div>
                  <div className="text-base">$19.00 /h No Travel Time</div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium text-gray-500">
                      Note to Temp Staff
                    </div>
                    <button className="text-xs text-red-600 hover:text-red-700">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    Currently there are no notes
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium text-gray-500">
                      Admin Note
                    </div>
                    <button className="text-xs text-red-600 hover:text-red-700">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    Currently there are no notes
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium text-gray-500">
                      Assignment
                    </div>
                    {/* actions button removed per requirements */}
                  </div>
                  <div className="text-red-600 font-medium">ID: {shiftId}</div>
                  <div className="text-base mt-2">
                    {shift?.assigned_staff_name || "Unassigned"}
                  </div>
                  {shift?.assigned_staff_phone && (
                    <div className="text-sm text-gray-600">
                      {shift.assigned_staff_phone}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Assignment Status
                  </div>
                  <div>{shift?.state || "Unassigned"}</div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-base font-medium">Temp Staff</div>
                    <div className="text-sm text-gray-600">
                      Showing Any Temp Staff in the Hospitality Ottawa division
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search temp staff..."
                        value={tempStaffSearch}
                        onChange={(e) => setTempStaffSearch(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                    <button className="p-2 hover:bg-gray-100 rounded">
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                        />
                      </svg>
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded">
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                    <button className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                      Create
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                          <th className="px-3 py-2">Temp Staff</th>
                          <th className="px-3 py-2">Type</th>
                          <th className="px-3 py-2">Proximity</th>
                          <th className="px-3 py-2">Reliability</th>
                          <th className="px-3 py-2">Utilization</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {tempStaffList.map((staff, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2">{staff.name}</td>
                            <td className="px-3 py-2">{staff.type}</td>
                            <td className="px-3 py-2">{staff.proximity} km</td>
                            <td className="px-3 py-2">{staff.reliability}%</td>
                            <td className="px-3 py-2">
                              {staff.utilization}%
                              <div className="text-sm text-gray-500">
                                {staff.hours}h
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Division and Position Modal */}
      {showDivisionModal && (
        <DivisionPositionModal
          shift={shift}
          onClose={() => setShowDivisionModal(false)}
          onSave={(updatedShift) => {
            setShift(updatedShift);
            setShowDivisionModal(false);
          }}
          BASE_URL={BASE_URL}
          overlayZClass={divisionOverlayZClass}
        />
      )}

      {unassignSaveModal.open && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-40 ${unassignOverlayZClass} flex items-center justify-center p-4`}
          onClick={() => {
            if (unassignSaveModal.submitting) return;
            unassignPendingSaveRef.current = null;
            setUnassignSaveModal((s) => ({ ...s, open: false }));
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Unassign Shift</h3>
            {unassignSaveModal.staffDisplayName ? (
              <p className="text-sm text-gray-600 mb-4">
                {unassignSaveModal.staffDisplayName}
              </p>
            ) : (
              <div className="mb-4" />
            )}

            <div className="space-y-4 text-sm">
              <div>
                <div className="text-gray-600 mb-2">
                  Count towards reliability
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={Boolean(unassignSaveModal.countTowardsReliability)}
                      onChange={() =>
                        setUnassignSaveModal((p) => ({
                          ...p,
                          countTowardsReliability: true,
                        }))
                      }
                      disabled={unassignSaveModal.submitting}
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!unassignSaveModal.countTowardsReliability}
                      onChange={() =>
                        setUnassignSaveModal((p) => ({
                          ...p,
                          countTowardsReliability: false,
                        }))
                      }
                      disabled={unassignSaveModal.submitting}
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Reason</div>
                <div className="flex items-center gap-6 flex-wrap">
                  {["Sick", "MIA/No Show", "Other"].map((r) => (
                    <label key={r} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={unassignSaveModal.reason === r}
                        onChange={() =>
                          setUnassignSaveModal((p) => ({ ...p, reason: r }))
                        }
                        disabled={unassignSaveModal.submitting}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">24 hour notice</div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={unassignSaveModal.notice24h === "yes"}
                      onChange={() =>
                        setUnassignSaveModal((p) => ({ ...p, notice24h: "yes" }))
                      }
                      disabled={unassignSaveModal.submitting}
                    />
                    Yes, notification more than 24 hours
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={unassignSaveModal.notice24h === "no"}
                      onChange={() =>
                        setUnassignSaveModal((p) => ({ ...p, notice24h: "no" }))
                      }
                      disabled={unassignSaveModal.submitting}
                    />
                    No, notification less than 24 hours
                  </label>
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Availability</div>
                <div className="space-y-2">
                  {unassignAvailabilityOptions.map((opt) => (
                    <label
                      key={opt.key}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        checked={unassignSaveModal.availability === opt.key}
                        onChange={() =>
                          setUnassignSaveModal((p) => ({
                            ...p,
                            availability: opt.key,
                          }))
                        }
                        disabled={unassignSaveModal.submitting}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Note</div>
                <input
                  type="text"
                  value={unassignSaveModal.note}
                  onChange={(e) =>
                    setUnassignSaveModal((p) => ({ ...p, note: e.target.value }))
                  }
                  disabled={unassignSaveModal.submitting}
                  className="w-full border-b border-gray-300 focus:outline-none focus:border-red-500 py-1"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
                onClick={() => {
                  if (unassignSaveModal.submitting) return;
                  unassignPendingSaveRef.current = null;
                  setUnassignSaveModal((p) => ({ ...p, open: false }));
                }}
                disabled={unassignSaveModal.submitting}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                onClick={submitUnassignModalAndSave}
                disabled={unassignSaveModal.submitting}
              >
                {unassignSaveModal.submitting
                  ? "UNASSIGNING..."
                  : unassignSaveModal.countTowardsReliability
                    ? "UNASSIGN & REDUCE RELIABILITY"
                    : "UNASSIGN"}
              </button>
            </div>
          </div>
        </div>
      )}

      {datetimePickerTarget && (
        <DateTimePickerPanel
          key={datetimePickerTarget}
          value={
            datetimePickerTarget === "start"
              ? shiftDisplayToDate(formData.startDate)
              : shiftDisplayToDate(
                  formData.endDate || formData.startDate,
                )
          }
          mode={datetimePickerTarget === "start" ? "from" : "to"}
          onChange={(date) => {
            const display = formatAppDateTime(date);
            if (datetimePickerTarget === "start") {
              handleInputChange("startDate", display);
            } else {
              handleInputChange("endDate", display);
            }
          }}
          onDone={() => setDatetimePickerTarget(null)}
          onContinue={() => setDatetimePickerTarget("end")}
          onBack={() => setDatetimePickerTarget("start")}
          minDateTime={shiftDatePickerMin}
        />
      )}

      {showActualsHistoryModal && (
        <div
          className={`fixed inset-0 bg-black/50 ${actualsOverlayZClass} flex items-center justify-center p-6`}
          onClick={() => {
            if (settingActiveActualId) return;
            setShowActualsHistoryModal(false);
            setHistoryRowMenuId(null);
          }}
        >
          <div
            className="actuals-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="actuals-history-modal__header">
              <h2>Shift Actuals</h2>
            </div>
            <div className="actuals-history-modal__meta actuals-modal__meta">
              <div className="actuals-modal__meta-text">
                <div className="actuals-history-modal__id">
                  ID: {getShiftAssignedAccountId(shift) ?? "—"}
                </div>
                <div className="actuals-history-modal__subtitle">
                  {shift?.position_name || shift?.positionName || "Position"}
                  {shift?.client_name || shift?.clientName
                    ? ` ${shift.client_name || shift.clientName}`
                    : ""}
                </div>
                <div className="actuals-history-modal__name">
                  {shift?.assigned_staff_name ||
                    shift?.staffDisplay ||
                    (shift?.first_name && shift?.last_name
                      ? `${shift.first_name} ${shift.last_name}`
                      : "Assigned staff")}
                </div>
                <div className="actuals-history-modal__section">
                  <div className="actuals-modal__label">Original Schedule</div>
                  <div className="actuals-modal__line">
                    {shift?.start_date && shift?.end_date
                      ? `${formatShiftDateTime(shift.start_date)} - ${formatShiftDateTime(shift.end_date, false)}`
                      : "—"}
                  </div>
                  {shift?.start_date && shift?.end_date ? (
                    <div className="actuals-history-modal__duration">
                      {formatDurationAfterBreak(
                        shift.start_date,
                        shift.end_date,
                        shift.unpaid_break,
                      )}
                    </div>
                  ) : null}
                </div>
                {shift?.checkin_date ? (
                  <div className="actuals-history-modal__section">
                    <div className="actuals-modal__label">Check in time</div>
                    <div className="actuals-modal__line">
                      {shift?.checkout_date
                        ? `${formatShiftDateTime(shift.checkin_date)} - ${formatShiftDateTime(shift.checkout_date, false)}`
                        : formatShiftDateTime(shift.checkin_date)}
                    </div>
                    {shift?.checkout_date ? (
                      <div className="actuals-history-modal__duration">
                        {formatDurationAfterBreak(
                          shift.checkin_date,
                          shift.checkout_date,
                          shift.checkin_unpaid_break_minutes ??
                            shift.checkinUnpaidBreakMinutes,
                        )}
                      </div>
                    ) : null}
                    {actualsSubmitted ? (
                      <span
                        className={`status-badge ${checkInTimeBadgeClass} mt-1 inline-block`}
                      >
                        {checkInTimeBadgeLabel}
                      </span>
                    ) : null}
                  </div>
                ) : actualsSubmitted ? (
                  <span
                    className={`status-badge ${checkInTimeBadgeClass} mt-1 inline-block`}
                  >
                    {checkInTimeBadgeLabel}
                  </span>
                ) : null}
              </div>
              <StaffAvatarCircle
                accountId={
                  getShiftAssignedAccountId(shift) ??
                  actualsAvatarStaff.accountId
                }
                employee={actualsAvatarStaff.employee}
                name={
                  shift?.assigned_staff_name ||
                  shift?.staffDisplay ||
                  (shift?.first_name && shift?.last_name
                    ? `${shift.first_name} ${shift.last_name}`
                    : "")
                }
                className="actuals-modal__avatar"
                textClassName="text-2xl font-medium text-white"
                emptyLabel="?"
              />
            </div>

            <div className="actuals-history-modal__table-wrap">
              {actualsHistoryLoading ? (
                <div className="actuals-history-modal__loading">Loading…</div>
              ) : (
                <table className="actuals-history-table">
                  <thead>
                    <tr>
                      <th>Currently Active</th>
                      <th>Account</th>
                      <th>Date</th>
                      <th>Actuals</th>
                      <th>Notes</th>
                      {!isClosedWeek && canWriteShiftActual ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {actualsHistory.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isClosedWeek || !canWriteShiftActual ? 5 : 6}
                          className="actuals-history-empty"
                        >
                          No actual submissions yet.
                        </td>
                      </tr>
                    ) : (
                      actualsHistory.map((row) => {
                        const rowId = row.id;
                        const accountLabel = String(
                          row.submittedBy?.name ||
                            [row.submittedBy?.firstName, row.submittedBy?.lastName]
                              .filter(Boolean)
                              .join(" ") ||
                            row.accountName ||
                            "",
                        ).trim();
                        const accountType = String(
                          row.submittedBy?.type || row.accountType || "",
                        ).trim();
                        return (
                          <tr key={rowId}>
                            <td className="actuals-history-active">
                              {row.isActive ? "✓" : ""}
                            </td>
                            <td>
                              <div>{accountLabel || "—"}</div>
                              {accountType ? (
                                <div className="actuals-history-account-type">
                                  {formatAccountTypeLabel(accountType)}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {row.createDate
                                ? format(
                                    new Date(row.createDate),
                                    "MMM d, yyyy h:mm a",
                                  )
                                : "—"}
                            </td>
                            <td>
                              <ShiftActualHistoryCell row={row} shift={shift} />
                            </td>
                            <td>{row.note || ""}</td>
                            {canWriteShiftActual && !isClosedWeek ? (
                            <td className="actuals-history-actions">
                              {!row.isActive ? (
                                <div className="actuals-history-menu-wrap">
                                  <button
                                    type="button"
                                    className="actuals-history-menu-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoryRowMenuId((prev) =>
                                        prev === rowId ? null : rowId,
                                      );
                                    }}
                                  >
                                    ⋮
                                  </button>
                                  {historyRowMenuId === rowId ? (
                                    <button
                                      type="button"
                                      className="actuals-history-set-active"
                                      disabled={
                                        settingActiveActualId === rowId
                                      }
                                      onClick={() => handleSetActiveActual(rowId)}
                                    >
                                      {settingActiveActualId === rowId
                                        ? "Setting…"
                                        : "Set as Actual"}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className="actuals-history-modal__footer">
              <button
                type="button"
                className="actuals-history-modal__done"
                onClick={() => {
                  setShowActualsHistoryModal(false);
                  setHistoryRowMenuId(null);
                }}
              >
                DONE
              </button>
            </div>
          </div>
        </div>
      )}

      {showActualsModal && canWriteShiftActual && (
        <div
          className={`fixed inset-0 bg-black/50 ${actualsOverlayZClass} flex items-center justify-center p-6`}
          onClick={() => {
            if (actualsSubmitting) return;
            setShowActualsModal(false);
          }}
        >
          <div
            className="actuals-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="actuals-modal__header">
              <h2>{actualsSubmitted ? "Modify Shift Actuals" : "Submit Shift Actuals"}</h2>
            </div>

            <div className="actuals-modal__body">
              <div className="actuals-modal__meta">
                <div className="actuals-modal__meta-text">
                  <div className="actuals-modal__id">
                    ID: {getShiftAssignedAccountId(shift) ?? "—"}
                  </div>
                  <div className="actuals-modal__location">
                    {shift?.location_name || "Location"}
                  </div>
                  <div className="actuals-modal__staff">
                    {shift?.assigned_staff_name ||
                      shift?.staffDisplay ||
                      (shift?.first_name && shift?.last_name
                        ? `${shift.first_name} ${shift.last_name}`
                        : "Assigned staff")}
                  </div>
                </div>
                <StaffAvatarCircle
                  accountId={
                    getShiftAssignedAccountId(shift) ??
                    actualsAvatarStaff.accountId
                  }
                  employee={actualsAvatarStaff.employee}
                  name={
                    shift?.assigned_staff_name ||
                    shift?.staffDisplay ||
                    (shift?.first_name && shift?.last_name
                      ? `${shift.first_name} ${shift.last_name}`
                      : "")
                  }
                  className="actuals-modal__avatar"
                  textClassName="text-2xl font-medium text-white"
                  emptyLabel="?"
                />
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Original Schedule</div>
                <div className="actuals-modal__line">
                  {shift?.start_date && shift?.end_date
                    ? `${formatShiftInstantDisplay(shift.start_date, { compact: true })} – ${formatShiftInstantDisplay(shift.end_date, { compact: true })}`
                    : "Unavailable"}
                </div>
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Check in time</div>
                <div className="actuals-modal__line">
                  {shift?.checkin_date && shift?.checkout_date
                    ? `${formatShiftInstantDisplay(shift.checkin_date, { compact: true })} – ${formatShiftInstantDisplay(shift.checkout_date, { compact: true })}`
                    : shift?.checkin_date
                      ? formatShiftInstantDisplay(shift.checkin_date, { compact: true })
                      : "No Checkin"}
                </div>
                <span
                  className={`status-badge ${checkInTimeBadgeClass}`}
                >
                  {checkInTimeBadgeLabel}
                </span>
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Actual Time</div>
                <div className="actuals-modal__options">
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="actualState"
                      checked={actualsForm.actualState === "as_scheduled"}
                      onChange={() =>
                        handleActualsFormChange("actualState", "as_scheduled")
                      }
                    />
                    <span>As Scheduled</span>
                  </label>
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="actualState"
                      checked={actualsForm.actualState === "different"}
                      onChange={() =>
                        handleActualsFormChange("actualState", "different")
                      }
                    />
                    <span>Different</span>
                  </label>
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="actualState"
                      checked={actualsForm.actualState === "no_show"}
                      onChange={() =>
                        handleActualsFormChange("actualState", "no_show")
                      }
                    />
                    <span>No Show</span>
                  </label>
                </div>
              </div>

              {actualsForm.actualState === "different" && (
                <div className="actuals-modal__different">
                  <div className="actuals-modal__field-grid">
                    <label className="actuals-modal__field">
                      <span>Start*</span>
                      <input
                        type="datetime-local"
                        value={actualsForm.actualStartDate}
                        onChange={(e) =>
                          handleActualsFormChange(
                            "actualStartDate",
                            e.target.value,
                          )
                        }
                      />
                    </label>
                    <label className="actuals-modal__field">
                      <span>End*</span>
                      <input
                        type="datetime-local"
                        min={actualsForm.actualStartDate || undefined}
                        value={actualsForm.actualEndDate}
                        onChange={(e) =>
                          handleActualsFormChange(
                            "actualEndDate",
                            e.target.value,
                          )
                        }
                      />
                    </label>
                  </div>
                  <label className="actuals-modal__field">
                    <span>Break*</span>
                    <div className="actuals-modal__break">
                      <input
                        type="number"
                        min="0"
                        value={actualsForm.actualUnpaidBreakMinutes}
                        onChange={(e) =>
                          handleActualsFormChange(
                            "actualUnpaidBreakMinutes",
                            e.target.value,
                          )
                        }
                      />
                      <span>min</span>
                    </div>
                  </label>
                </div>
              )}

              <label className="actuals-modal__field">
                <span>Notes</span>
                <textarea
                  value={actualsForm.note}
                  onChange={(e) =>
                    handleActualsFormChange("note", e.target.value)
                  }
                  rows={3}
                />
              </label>
            </div>

            <div className="actuals-modal__footer">
              <button
                type="button"
                className="actuals-modal__submit"
                onClick={handleSubmitActuals}
                disabled={actualsSubmitting}
              >
                {actualsSubmitting ? "SUBMITTING..." : "SUBMIT"}
              </button>
              <button
                type="button"
                className="actuals-modal__cancel"
                onClick={() => setShowActualsModal(false)}
                disabled={actualsSubmitting}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      <DuplicateShiftModal
        zIndexClass={duplicateZIndexClass}
        open={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        shiftId={shiftId}
        shift={shift}
        shiftPatch={duplicateShiftPatch}
        requestDefaults={requestDefaults}
        onSaveBeforeDuplicate={() => handleSave(false, false)}
        onDuplicateSuccess={() => {
          if (typeof onRefresh === "function") onRefresh();
          if (typeof onHide === "function") onHide();
        }}
      />

      <ManualCheckInModal
        show={showManualCheckIn}
        shift={shift}
        onClose={() => setShowManualCheckIn(false)}
        onSave={handleManualAttendance}
        onRoundDate={(date) =>
          roundManualAttendanceDate(date, manualAttendanceMode, shift)
        }
        mode={manualAttendanceMode}
        saving={manualAttendanceSaving}
      />

      <CarpoolEditModal
        overlayZClass={carpoolOverlayZClass}
        isOpen={carpoolViewer.open}
        onClose={() => setCarpoolViewer((s) => ({ ...s, open: false }))}
        initialName={carpoolViewer.name}
        initialNote={carpoolViewer.note}
        readOnly={isClosedWeek}
        saving={carpoolSaving}
        onSave={handleCarpoolSave}
      />
    </>
  );
};

export default ShiftDetailModal;
